import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const location = useLocation();

  const services = [
    { name: 'Scratch & Paint Repair', path: '/services/scratch-paint-repair' },
    { name: 'Dent Repair & Removal', path: '/services/dent-repair' },
    { name: 'Alloy Wheel Repair', path: '/services/alloy-wheel-repair' },
    { name: 'Bumper Repair', path: '/services/bumper-repair' },
    { name: 'Ceramic Coating', path: '/services/ceramic-coating' },
    { name: 'Stone Chip & Touch Up', path: '/services/stone-chip-touch-up' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-dark-navy shadow-lg sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-bmw-blue text-white py-2 sm:py-2">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap justify-between items-center gap-2 sm:gap-0 text-xs sm:text-sm">
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
              <a href="tel:08000452446" className="flex items-center gap-1 hover:text-off-white transition">
                <Phone size={12} className="sm:w-3.5 sm:h-3.5" />
                <span className="text-xs sm:text-sm">0800 045 2446</span>
              </a>
              <span className="hidden sm:inline">|</span>
              <a href="tel:08000453447" className="flex items-center gap-1 hover:text-off-white transition">
                <Phone size={12} className="sm:w-3.5 sm:h-3.5" />
                <span className="text-xs sm:text-sm">0800 045 3447</span>
              </a>
            </div>
            <div className="text-xs sm:text-sm">
              <span className="font-medium">Mon-Sat: 09:00 – 17:00</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img 
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="GBW Bodyworks Logo" 
              className="h-10 sm:h-12 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium transition ${
                isActive('/') ? 'text-bmw-blue' : 'text-white hover:text-bmw-blue'
              }`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <button className="flex items-center gap-1 font-medium text-white hover:text-bmw-blue transition">
                Services
                <ChevronDown size={16} />
              </button>
              {servicesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2">
                  {services.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      className="block px-4 py-2 text-dark-navy hover:bg-bmw-blue hover:text-white transition"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`font-medium transition ${
                isActive('/about') ? 'text-bmw-blue' : 'text-white hover:text-bmw-blue'
              }`}
            >
              About Us
            </Link>

            <Link
              to="/contact"
              className={`font-medium transition ${
                isActive('/contact') ? 'text-bmw-blue' : 'text-white hover:text-bmw-blue'
              }`}
            >
              Contact
            </Link>

            <Link
              to="/contact"
              className="bg-racing-red text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white hover:text-bmw-blue transition"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-2">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 px-4 rounded ${
                isActive('/') ? 'bg-bmw-blue text-white' : 'text-white hover:bg-dark-teal'
              }`}
            >
              Home
            </Link>

            <div>
              <button
                onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                className="w-full flex items-center justify-between py-2 px-4 text-white hover:bg-dark-teal rounded"
              >
                Services
                <ChevronDown size={16} />
              </button>
              {servicesDropdownOpen && (
                <div className="ml-4 mt-1 space-y-1">
                  {services.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 px-4 text-soft-grey hover:text-bmw-blue"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 px-4 rounded ${
                isActive('/about') ? 'bg-bmw-blue text-white' : 'text-white hover:bg-dark-teal'
              }`}
            >
              About Us
            </Link>

            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 px-4 rounded ${
                isActive('/contact') ? 'bg-bmw-blue text-white' : 'text-white hover:bg-dark-teal'
              }`}
            >
              Contact
            </Link>

            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="block bg-racing-red text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition text-center mt-4"
            >
              Get Quote
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
